import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Key, Loader2 } from 'lucide-react';

interface PinProtectedRouteProps {
  children: React.ReactNode;
}

export default function PinProtectedRoute({ children }: PinProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [requiresPin, setRequiresPin] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    checkIfRouteRequiresPin();
  }, [location.pathname]);

  const checkIfRouteRequiresPin = async () => {
    try {
      const { data, error } = await supabase
        .from('os_pin_protection')
        .select('protected_routes')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching PIN protection:', error);
        throw error;
      }

      if (data && data.protected_routes && data.protected_routes.length > 0) {
        // Check if current route matches any protected route pattern
        const currentPath = location.pathname;

        const matches = data.protected_routes.some((protectedRoute: string) => {
          // Handle dynamic routes like /meetings/:id
          const pattern = protectedRoute.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          const exactMatch = regex.test(currentPath);
          const prefixMatch = currentPath.startsWith(protectedRoute);
          return exactMatch || prefixMatch;
        });
        setRequiresPin(matches);
        
        // Check if PIN is already verified in session
        const verifiedRoutes = sessionStorage.getItem('pinVerifiedRoutes') || '[]';
        const verified = JSON.parse(verifiedRoutes);

        if (verified.includes(currentPath)) {
          setIsVerified(true);
        } else {
          setIsVerified(false);
        }
      } else {
        setRequiresPin(false);
        setIsVerified(true);
      }
    } catch (error) {
      console.error('Error checking PIN requirement:', error);
      setRequiresPin(false);
      setIsVerified(true);
    } finally {
      setIsChecking(false);
    }
  };

  const handleVerifyPin = async () => {
    if (!pin || pin.length !== 4) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-pin', {
        body: { pin }
      });

      if (error) throw error;

      if (data?.verified) {
        setIsVerified(true);
        // Store verified route in session
        const verifiedRoutes = JSON.parse(sessionStorage.getItem('pinVerifiedRoutes') || '[]');
        if (!verifiedRoutes.includes(location.pathname)) {
          verifiedRoutes.push(location.pathname);
          sessionStorage.setItem('pinVerifiedRoutes', JSON.stringify(verifiedRoutes));
        }
        toast.success('PIN verified successfully');
        setPin('');
      } else {
        toast.error('Invalid PIN');
        setPin('');
      }
    } catch (error: any) {
      console.error('Error verifying PIN:', error);
      toast.error(`Failed to verify PIN: ${error.message || 'Unknown error'}`);
      setPin('');
    } finally {
      setIsVerifying(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (requiresPin && !isVerified) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              PIN Required
            </DialogTitle>
            <DialogDescription>
              This page requires PIN verification to access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin-input">Enter 4-digit PIN</Label>
              <Input
                id="pin-input"
                type="password"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="0000"
                className="font-mono text-2xl text-center tracking-widest"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pin.length === 4) {
                    handleVerifyPin();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleVerifyPin}
                disabled={isVerifying || pin.length !== 4}
                className="flex-1"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify PIN'
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Go Back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return <>{children}</>;
}
